import {
  SelectOptionsMessage as SelectOptionsMessageType,
  Conversation,
} from '@stripe-internal/conversations-client';
import {useCallback, useMemo, useState} from 'react';
import {defineMessages, FormattedMessage, MessageDescriptor} from 'react-intl';
import {messages} from './messages';
import {ChatSelectOptions} from '../../../Chat';

const selectMessages: Record<string, MessageDescriptor> = defineMessages({
  topic_selection: {
    id: 'conversations_ui.conversation.log.messages.selectOptions.labels.topic',
    description: 'Label for the topic selector in the select options message',
    defaultMessage: 'Select a topic…',
  },
  merchant_selection: {
    id: 'conversations_ui.conversation.log.messages.selectOptions.labels.merchant',
    description:
      'Label for the merchant selector in the select options message',
    defaultMessage: 'Select an account…',
  },
});

type Props = {
  conversation: Conversation;
  message: SelectOptionsMessageType;
  disableInteraction?: boolean;
};

interface Option {
  value: string;
  label: string;
}

export const SelectOptionsMessage = ({
  conversation,
  message,
  disableInteraction,
}: Props) => {
  const [selectedOptionId, setSelectedOptionId] = useState<string | undefined>(
    message.selectedOptionId || undefined,
  );
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const options = useMemo(
    () =>
      message.options.map((option) => {
        return {
          value: option.id,
          label: option.label,
        };
      }),
    [message],
  );

  const onSelected = useCallback(
    async (option: Option) => {
      if (submitting || disableInteraction) {
        return;
      }
      setSubmitting(true);

      const resp = await conversation.selectOption({
        message,
        selectedOptionId: option.value,
      });

      if ('error' in resp) {
        setError(resp.error);
        setSelectedOptionId(undefined);
      } else if (resp.code === 205) {
        setSelectedOptionId(undefined);
      } else {
        setSelectedOptionId(option.value);
      }

      setSubmitting(false);
    },
    [conversation, message, submitting, disableInteraction],
  );

  const label = selectMessages[message.purpose || ''];

  return (
    <>
      <ChatSelectOptions
        label={label}
        options={options}
        onSelected={onSelected}
        selectedOptionId={message.selectedOptionId ?? selectedOptionId}
        defaultOptionId={message.defaultOptionId}
        error={error ? <FormattedMessage {...messages.sendError} /> : null}
        submitting={submitting}
        disabled={submitting || disableInteraction}
      />
    </>
  );
};
